import { fetchUschovaDetail } from '../services/api/uschovy'

const toTrim = (value) => String(value ?? '').trim()
const formatDepth = (value) => {
  const n = Number(String(value ?? '').replace(',', '.'))
  return Number.isFinite(n) ? String(n).replace('.', ',') : ''
}
const toNum = (value, fallback = 0) => {
  const n = Number(value)
  return Number.isFinite(n) ? n : fallback
}
const toNullableNum = (value) => {
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}
const toDateKey = (value) => {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}
const getZakDateForDayCompare = (zak) => {
  const smer = toNum(zak?.smer, 0)
  if (smer === 2) return zak?.datum_vydeje || zak?.datum_prijmu || zak?.datum || null
  return zak?.datum_prijmu || zak?.datum || null
}
const getZakPrimaryDate = (zak) => zak?.datum_prijmu || zak?.datum || null

export const fetchStoragePrintData = async (zakazkaId) => {
  const id = toNum(zakazkaId, 0)
  if (id <= 0) throw new Error('Chybi ID zakazky pro tisk.')

  const detail = await fetchUschovaDetail(id)
  const vehicle = detail?.vehicle || {}
  const customer = detail?.customer || {}
  const zakazky = Array.isArray(detail?.zakazky) ? detail.zakazky : []
  const pneuByZak = Array.isArray(detail?.pneumatikyByZakazka) ? detail.pneumatikyByZakazka : []

  const zak = zakazky.find((z) => toNum(z?.id_zakazky) === id) || zakazky[0] || {}
  const anchorDateKey = toDateKey(getZakPrimaryDate(zak))
  const sameDayZakazky = anchorDateKey
    ? zakazky.filter((z) => toDateKey(getZakDateForDayCompare(z)) === anchorDateKey)
    : []

  const prijemZak =
    sameDayZakazky.find((z) => toNum(z?.smer) === 1) ||
    (toNum(zak?.smer) === 1 ? zak : null) ||
    null
  const vydejZak =
    sameDayZakazky.find((z) => toNum(z?.smer) === 2) ||
    (toNum(zak?.smer) === 2 ? zak : null) ||
    null

  const getPneuRowsForZak = (z) =>
    pneuByZak.find((row) => toNum(row?.id_zakazky) === toNum(z?.id_zakazky, 0))?.polozky || []

  const pneuRows =
    pneuByZak.find((z) => toNum(z?.id_zakazky) === toNum(zak?.id_zakazky, id))?.polozky ||
    []
  const mainPneu = pneuRows.find((p) => toTrim(p?.rozmer) || toTrim(p?.znacka) || toTrim(p?.model)) || pneuRows[0] || {}
  const mappedPneuRows = (Array.isArray(pneuRows) ? pneuRows : []).map((p, idx) => ({
    poradi: idx + 1,
    pozice: toTrim(p?.pozice),
    rozmer: toTrim(p?.rozmer),
    znacka: toTrim(p?.znacka),
    model: toTrim(p?.model),
    hloubka: formatDepth(p?.hloubka),
    sezona: toTrim(p?.sezona),
    disk: toTrim(p?.disk),
  }))
  const mapZakazkaForProtocol = (z, defaultLabel) => {
    if (!z) return null
    const zPneuRows = Array.isArray(getPneuRowsForZak(z)) ? getPneuRowsForZak(z) : []
    const firstPneu = zPneuRows[0] || {}
    const pneuSummary = zPneuRows
      .map((p) => [toTrim(p?.rozmer), toTrim(p?.znacka), toTrim(p?.model)].filter(Boolean).join(' '))
      .filter(Boolean)
      .filter((v, i, arr) => arr.indexOf(v) === i)
      .join(' | ')

    return {
      id: toNum(z?.id_zakazky, 0),
      cislo: toTrim(z?.cislo),
      datum_prijmu: z?.datum_prijmu || z?.datum || null,
      datum_vydeje: z?.datum_vydeje || null,
      datum: getZakDateForDayCompare(z),
      delkaUschovyDny: toNullableNum(z?.delka_uschovy_dny),
      smer: toNum(z?.smer, 0),
      typVydeje: toTrim(z?.typ_vydeje),
      tmps: z?.tmps,
      poklice: z?.poklice,
      srouby: z?.srouby,
      poznamka: toTrim(z?.poznamka),
      pneuPocet: zPneuRows.length || toNum(z?.pneu_pocet, 0),
      rozmer: toTrim(firstPneu?.rozmer),
      znacka: toTrim(firstPneu?.znacka),
      dezen: toTrim(firstPneu?.model),
      sezona: toTrim(firstPneu?.sezona),
      disk: toTrim(firstPneu?.disk),
      pneuSummary,
      label: defaultLabel,
    }
  }

  return {
    zakazkaId: id,
    cisloZakazky: toTrim(zak?.cislo),
    ean: toTrim(zak?.ean),
    datum: getZakPrimaryDate(zak),
    datum_prijmu: zak?.datum_prijmu || zak?.datum || null,
    datum_vydeje: zak?.datum_vydeje || null,
    spz: toTrim(vehicle?.spz),
    firma: toTrim(customer?.firma),
    jmeno: toTrim(customer?.jmeno),
    ulice: toTrim(customer?.ulice),
    psc: toTrim(customer?.psc),
    mesto: toTrim(customer?.obec),
    ico: toTrim(customer?.ico),
    email: toTrim(customer?.email),
    telefon: toTrim(customer?.telefon),
    rozmer: toTrim(mainPneu?.rozmer),
    znacka: toTrim(mainPneu?.znacka),
    dezen: toTrim(mainPneu?.model),
    sezona: toTrim(mainPneu?.sezona),
    disk: toTrim(mainPneu?.disk),
    tmps: zak?.tmps,
    poklice: zak?.poklice,
    srouby: zak?.srouby,
    poznamka: toTrim(zak?.poznamka),
    labelCount: Array.isArray(pneuRows) && pneuRows.length > 0 ? pneuRows.length : Math.max(1, toNum(vehicle?.pocet_pneu, 1)),
    pneuRows: mappedPneuRows,
    prijemZakazka: mapZakazkaForProtocol(prijemZak, 'Příjem'),
    vydejZakazka: mapZakazkaForProtocol(vydejZak, 'Výdej'),
  }
}
